import React, { useCallback, useMemo } from 'react';

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnDef as TanStackColumnDef,
  SortingState,
  PaginationState,
  RowSelectionState,
  Updater,
} from '@tanstack/react-table';

import TableBody from './TableBody';
import TableHeader from './TableHeader';
import TablePagination from './TablePagination/TablePagination';
import { TableProps, ColumnDef, TableRowData } from './types';

import EmptyState from '~/core/molecules/EmptyState';
import SkeletonTable from '~/core/molecules/SkeletonTable';
import { mapSelectedRows } from '~/core/molecules/Table/helpers';
import cn from '~/utils/cn';

const DEFAULT_PAGE_SIZE = 10;
const UNKNOWN_TOTAL_ROWS_VALUE = -1;

const Table = <T extends TableRowData>({
  data,
  columns,
  loading = false,
  pagination,
  sorting,
  selection,
  emptyState,
  className,
  onRowClick,
  onSelectionChange,
  onSortingChange,
  onPaginationChange,
}: TableProps<T>) => {
  // Internal getRowId implementation - always use row.id
  const getRowId = useCallback((row: T) => String(row.id), []);

  // Convert our column definition to TanStack format
  const convertToTanStackColumn = useCallback(
    (col: ColumnDef<T>): TanStackColumnDef<T> => ({
      id: col.id,
      header: typeof col.header === 'string' ? col.header : () => col.header,
      accessorKey: col.accessorKey as string,
      enableSorting: col.sortable && sorting?.enabled,
      cell: ({ getValue, row }) => {
        const val = getValue();
        return col.cell ? col.cell(val, row.original) : val;
      },
      size: col.width,
    }),
    [sorting?.enabled]
  );

  const tanStackColumns = useMemo<TanStackColumnDef<T>[]>(() => {
    return columns.filter((col) => col.visible !== false).map(convertToTanStackColumn);
  }, [columns, convertToTanStackColumn]);

  // Initialize sorting state
  const [sortingState, setSortingState] = React.useState<SortingState>(sorting?.defaultSort || []);

  // Initialize pagination state
  const [internalPaginationState, setInternalPaginationState] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pagination?.pageSize || DEFAULT_PAGE_SIZE,
  });

  const rowSelection = useMemo(() => {
    return selection?.selectedRows ? mapSelectedRows(selection.selectedRows) : {};
  }, [selection?.selectedRows]);

  // Determine if pagination is manual
  const isManualPagination = !!pagination?.manual;

  const paginationState = {
    ...internalPaginationState,
    // if pagination is manual, use the provided pageIndex and pageSize
    ...(isManualPagination
      ? {
          pageIndex: pagination.pageIndex || 0,
          pageSize: pagination?.pageSize || DEFAULT_PAGE_SIZE,
        }
      : {}),
  };

  // Calculate page count for manual pagination
  // please note: this behavior is based on this knowledge from TanStack Table documentation:
  // https://tanstack.com/table/v8/docs/guide/pagination#manual-server-side-pagination
  const pageCount = isManualPagination
    ? pagination?.totalRows === UNKNOWN_TOTAL_ROWS_VALUE
      ? UNKNOWN_TOTAL_ROWS_VALUE
      : Math.ceil((pagination?.totalRows || 0) / paginationState.pageSize)
    : undefined;

  // Create table instance
  const table = useReactTable({
    data,
    columns: tanStackColumns,
    getRowId,
    state: {
      sorting: sortingState,
      pagination: paginationState,
      rowSelection,
    },
    onSortingChange: (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === 'function' ? updater(sortingState) : updater;
      setSortingState(newSorting);
      if (onSortingChange) {
        onSortingChange(newSorting);
      }
    },
    onPaginationChange: (updater: Updater<PaginationState>) => {
      const newPagination = typeof updater === 'function' ? updater(paginationState) : updater;

      // For controlled pagination, the parent component handles state updates
      if (!isManualPagination) {
        setInternalPaginationState(newPagination);
      }

      // Always call the existing callback - this handles both manual and automatic pagination
      if (onPaginationChange) {
        onPaginationChange(newPagination.pageIndex + 1, newPagination.pageSize);
      }
    },
    onRowSelectionChange: (updater: Updater<RowSelectionState>) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;

      if (onSelectionChange) {
        // Calculate selected rows from the new selection state and data
        const selectedRows = data.filter((row) => {
          const rowId = getRowId(row);
          return newSelection[rowId];
        });
        onSelectionChange(selectedRows);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: pagination?.enabled ? getPaginationRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: selection?.enabled || false,
    enableMultiRowSelection: true,
    enableSorting: sorting?.enabled !== false,
    enableMultiSort: sorting?.multiSort || false,
    manualPagination: isManualPagination,
    pageCount: pageCount,
    manualSorting: false,
  });

  // Handle loading state
  if (loading) {
    return (
      <div className={cn('w-full', className)}>
        <SkeletonTable
          rowsCount={pagination?.pageSize || DEFAULT_PAGE_SIZE}
          columnsCount={columns.length + (selection?.enabled ? 1 : 0)}
        />
      </div>
    );
  }

  // Handle empty state
  if (!data.length) {
    return (
      <div className={cn('w-full', className)}>
        {emptyState || (
          <EmptyState title="No data available" description="There are no items to display in this table." />
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full overflow-x-auto rounded-md', className)}>
      {/* Table container with horizontal scroll */}

      <table className="mb-16 min-w-full shadow-xs">
        <TableHeader table={table} selection={selection} className="sticky top-0 z-10 rounded-md" />
        <TableBody table={table} selection={selection} onRowClick={onRowClick} />
      </table>

      {/* Pagination */}
      {pagination?.enabled && <TablePagination table={table} pagination={pagination} />}
    </div>
  );
};

export default Table;
